import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AI_CREDITS_STATUS_QUERY_KEY } from '@/hooks/queries/useAiCredits';
import type { CardDetails } from '@/components/billing/CardPaymentForm';

interface InitiateCreditPurchasePayload {
  credits: number;
  /** Required for mobile money; omitted for card payments. */
  phone_number?: string;
  /** Absent = mobile_money (legacy behaviour). */
  payment_method?: 'mobile_money' | 'card';
  /** Card details (NytroPay). Sent once over HTTPS, never stored client-side. */
  card?: CardDetails;
}

interface InitiateCreditPurchaseResponse {
  /** Null for a card charged without 3-D Secure (already completed). */
  payment_url: string | null;
  transaction_id: string;
  amount: number;
  credits: number;
  status?: string;
}

export function useInitiateCreditPurchase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: InitiateCreditPurchasePayload,
    ): Promise<InitiateCreditPurchaseResponse> => {
      const response = await api.post<InitiateCreditPurchaseResponse>(
        '/ai/credits/purchase/initiate',
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['ai', 'credits', 'transactions'] });
    },
  });
}
